import { useLocation } from "react-router-dom";
import { SVGName } from "../../../assets/svgs/GenericSVG";
import { useMainStore } from "../../../core/store/mainStore";
import {
  getAppointmentFlowScreens,
  SCREENS_ON_SHARED_STEP,
  usePracticeSelectionSkip,
} from "../../appointment/utils/appointmentFlow";
import { AppointmentBookingStepNames } from "./useHandleAppointmentBookingNavigation";

export type SideBarNavItemType = {
  index: number;
  label: string;
  iconName: SVGName;
  screen: AppointmentBookingStepNames;
  isRouteActive: boolean;
};

const SIDE_BAR_LABELS: Partial<
  Record<AppointmentBookingStepNames, { label: string; iconName: SVGName }>
> = {
  practice: { label: "SIDE_BAR_CHOOSE_PRACTICE_LABEL", iconName: "Location" },
  department: {
    label: "SIDE_BAR_CHOOSE_DEPARTMENT_LABEL",
    iconName: "Category",
  },
  type: {
    label: "SIDE_BAR_CHOOSE_REQUEST_TYPE_LABEL",
    iconName: "OptionsIcon",
  },
  recipeInfo: { label: "SIDE_BAR_RECIPE_UPLOAD_LABEL", iconName: "Document" },
  timeSlot: { label: "SIDE_BAR_TIME_SLOT_SELECTION_LABEL", iconName: "Clock" },
  preferences: {
    label: "SIDE_BAR_APPOINTMENT_INFORMATION_LABEL",
    iconName: "Info",
  },
  contact: {
    label: "SIDE_BAR_CONTACT_INFORMATION_LABEL",
    iconName: "ContactDataIcon",
  },
  success: { label: "SIDE_BAR_REQUEST_SENT_LABEL", iconName: "MailSend" },
  phoneNumber: {
    label: "SIDE_BAR_PHONE_NUMBER_INPUT_LABEL",
    iconName: "Phone",
  },
  cancellationAppointmentDateTime: {
    label: "SIDE_BAR_APPOINTMENT_INFORMATION_LABEL",
    iconName: "Info",
  },
  cancellationAppointmentContact: {
    label: "SIDE_BAR_CONTACT_INFORMATION_LABEL",
    iconName: "ContactDataIcon",
  },
  modificationAppointmentDateTime: {
    label: "SIDE_BAR_APPOINTMENT_INFORMATION_LABEL",
    iconName: "Info",
  },
  modificationAppointmentTimeSlots: {
    label: "SIDE_BAR_TIME_SLOT_SELECTION_LABEL",
    iconName: "Clock",
  },
  modificationAppointmentContact: {
    label: "SIDE_BAR_CONTACT_INFORMATION_LABEL",
    iconName: "ContactDataIcon",
  },
};

/**
 * PF-5480: the sidebar mirrors the same flow list as the progress bar, so a skipped practice selection or a
 * hidden preferences step disappears from both at once. `index` is the array index on purpose — SideBar
 * compares it against a findIndex result, so a hardcoded number would leave no step active once one is
 * dropped.
 */
export const useHandleSideBarNavItems = () => {
  const { isSkipped } = usePracticeSelectionSkip();
  const flowScreens = useMainStore((state) =>
    getAppointmentFlowScreens(state, isSkipped),
  );
  const currentScreen = useLocation()
    .pathname.split("/")
    .pop() as AppointmentBookingStepNames;
  const activeScreen = SCREENS_ON_SHARED_STEP[currentScreen] ?? currentScreen;

  const sideBarNavItems: SideBarNavItemType[] = flowScreens.flatMap(
    (screen, index) => {
      const labels = SIDE_BAR_LABELS[screen];
      if (!labels) return [];
      return [
        { index, screen, ...labels, isRouteActive: screen === activeScreen },
      ];
    },
  );

  const currentActiveIndex = sideBarNavItems.findIndex(
    (item) => item.isRouteActive,
  );

  return { sideBarNavItems, currentActiveIndex };
};
